import { useState, FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import StatusBadge from '../../components/StatusBadge';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import { clientApi } from '../../services/client';
import type { VendorDirectoryItem, VendorConnection } from '../../types';
import extractApiError from '../../utils/extractApiError';
import { toGGScore, getScoreColor, getScoreColorClass } from '../../utils/score';

const scoreColor = (score: number) => getScoreColorClass(getScoreColor(score));

type FilterTab = 'all' | 'pending';

export default function ClientVendorDirectory() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<FilterTab>('all');
  const [toast, setToast] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteError, setInviteError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['client', 'directory', search, page],
    queryFn: () =>
      clientApi.getDirectory({ search, page: String(page) }).then((r) => r.data),
  });

  // Load all existing connections
  const { data: connectionsData } = useQuery({
    queryKey: ['client', 'connections', 'all'],
    queryFn: () => clientApi.getConnections({ page_size: '1000' }).then((r) => r.data),
  });

  const connections: VendorConnection[] = connectionsData?.results ?? [];
  const connectionMap = new Map<string, { status: VendorConnection['status']; id: string }>();
  connections.forEach((c) => {
    connectionMap.set(c.vendor_org.id, { status: c.status, id: c.id });
  });

  const pendingConnections = connections.filter((c) => c.status === 'pending');

  const showToast = (msg: string, duration = 3000) => {
    setToast(msg);
    setTimeout(() => setToast(null), duration);
  };

  const connectMutation = useMutation({
    mutationFn: (vendorOrgId: string) => clientApi.requestConnection(vendorOrgId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', 'connections'] });
      showToast('Connection request sent!');
    },
    onError: (err: unknown) => {
      showToast(extractApiError(err), 4000);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (connectionId: string) => clientApi.cancelConnection(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client', 'connections'] });
      showToast('Connection request canceled.');
    },
    onError: (err: unknown) => {
      showToast(extractApiError(err), 4000);
    },
  });

  const inviteMutation = useMutation({
    mutationFn: (email: string) => clientApi.sendPlatformInvite(email),
    onSuccess: (_data, email) => {
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteError(null);
      showToast(`Invitation sent to ${email}`);
    },
    onError: (err: unknown) => {
      setInviteError(extractApiError(err));
    },
  });

  const handleInviteSubmit = (e: FormEvent) => {
    e.preventDefault();
    setInviteError(null);
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setInviteError('Please enter a valid email address.');
      return;
    }
    inviteMutation.mutate(email);
  };

  const vendors: VendorDirectoryItem[] = data?.results ?? [];
  const totalPages = data ? Math.ceil(data.count / 20) : 1;

  return (
    <>
      <div className="flex items-start justify-between mb-0">
        <PageHeader
          title="Find Vendors"
          description="Browse security vendors and send connection requests"
        />
        <button
          onClick={() => setShowInviteModal(true)}
          className="flex-shrink-0 px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700 whitespace-nowrap"
        >
          Invite Vendor
        </button>
      </div>

      {/* Toast */}
      {toast && (
        <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
          <p className="text-sm text-blue-700">{toast}</p>
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 text-sm font-medium rounded-xl transition-colors ${
            filter === 'all'
              ? 'bg-brand-600 text-white'
              : 'bg-white text-slate-600 border-2 border-slate-200 hover:bg-slate-50'
          }`}
        >
          All Vendors
        </button>
        <button
          onClick={() => setFilter('pending')}
          className={`px-4 py-2 text-sm font-medium rounded-xl transition-colors flex items-center gap-2 ${
            filter === 'pending'
              ? 'bg-brand-600 text-white'
              : 'bg-white text-slate-600 border-2 border-slate-200 hover:bg-slate-50'
          }`}
        >
          Pending Requests
          {pendingConnections.length > 0 && (
            <span className={`inline-flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full ${
              filter === 'pending' ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-700'
            }`}>
              {pendingConnections.length}
            </span>
          )}
        </button>
      </div>

      {/* Pending Requests View */}
      {filter === 'pending' && (
        pendingConnections.length === 0 ? (
          <EmptyState
            title="No pending requests"
            description="You don't have any pending connection requests right now."
          />
        ) : (
          <div className="space-y-3">
            {pendingConnections.map((conn) => (
              <div
                key={conn.id}
                className="rounded-2xl border border-slate-100 bg-white p-4 flex items-center justify-between gap-4"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-slate-900">{conn.vendor_org.name}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Sent {new Date(conn.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={`text-sm font-bold ${scoreColor(conn.vendor_org.score)}`}>
                    {toGGScore(conn.vendor_org.score)}
                  </span>
                  <StatusBadge label="Pending" variant="warning" />
                </div>
                <button
                  onClick={() => cancelMutation.mutate(conn.id)}
                  disabled={cancelMutation.isPending}
                  className="flex-shrink-0 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50"
                >
                  {cancelMutation.isPending ? 'Canceling...' : 'Cancel Request'}
                </button>
              </div>
            ))}
          </div>
        )
      )}

      {/* All Vendors View */}
      {filter === 'all' && (
        <>
          {/* Search bar */}
          <div className="mb-6">
            <input
              type="text"
              placeholder="Search by name, city, or state..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full max-w-md rounded-xl border-2 border-slate-200 px-4 py-2.5 text-sm placeholder-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : vendors.length === 0 ? (
            <EmptyState
              title="No vendors found"
              description={search ? 'Try adjusting your search.' : 'No vendor organizations are registered yet.'}
            />
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {vendors.map((vendor) => {
                  const conn = connectionMap.get(vendor.id);
                  const connStatus = conn?.status;
                  const connId = conn?.id;
                  return (
                    <div
                      key={vendor.id}
                      className="rounded-2xl border border-slate-100 bg-white p-5 flex flex-col gap-3"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-sm font-semibold text-slate-900">{vendor.name}</h3>
                          {(vendor.city || vendor.state) && (
                            <p className="text-xs text-slate-500 mt-0.5">
                              {[vendor.city, vendor.state].filter(Boolean).join(', ')}
                            </p>
                          )}
                        </div>
                        <span className={`text-lg font-bold ${scoreColor(vendor.score)}`}>
                          {toGGScore(vendor.score)}
                        </span>
                      </div>

                      <div className="text-xs text-slate-500 space-y-1">
                        {vendor.phone && <p>Phone: {vendor.phone}</p>}
                        {vendor.website && (
                          <p className="truncate">
                            Web: {vendor.website}
                          </p>
                        )}
                      </div>

                      <div className="mt-auto pt-2">
                        {connStatus === 'active' ? (
                          <span className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-600">
                            Connected
                          </span>
                        ) : connStatus === 'pending' ? (
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-600">
                              Pending
                            </span>
                            <button
                              onClick={() => connId && cancelMutation.mutate(connId)}
                              disabled={cancelMutation.isPending}
                              className="text-xs text-red-500 hover:text-red-700 underline"
                            >
                              Undo
                            </button>
                          </div>
                        ) : connStatus === 'rejected' ? (
                          <button
                            onClick={() => connectMutation.mutate(vendor.id)}
                            disabled={connectMutation.isPending}
                            className="w-full px-4 py-2 text-sm font-medium text-slate-700 bg-white border-2 border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-50"
                          >
                            {connectMutation.isPending ? 'Sending...' : 'Reconnect'}
                          </button>
                        ) : (
                          <button
                            onClick={() => connectMutation.mutate(vendor.id)}
                            disabled={connectMutation.isPending}
                            className="w-full px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700 disabled:opacity-50"
                          >
                            {connectMutation.isPending ? 'Sending...' : 'Connect'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <p className="text-sm text-slate-500">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage(page - 1)}
                      disabled={page <= 1}
                      className="px-3 py-1 text-sm border-2 border-slate-200 rounded-xl disabled:opacity-50 hover:bg-slate-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page >= totalPages}
                      className="px-3 py-1 text-sm border-2 border-slate-200 rounded-xl disabled:opacity-50 hover:bg-slate-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Invite Vendor Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Invite a Vendor</h3>
              <button
                onClick={() => { setShowInviteModal(false); setInviteEmail(''); setInviteError(null); }}
                className="text-slate-400 hover:text-slate-600 text-xl leading-none"
              >
                &times;
              </button>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Send a signup invitation to a security vendor. They will receive an email with a link to join GuardGrade.
            </p>
            <form onSubmit={handleInviteSubmit}>
              <label htmlFor="invite_email" className="block text-sm font-medium text-slate-700 mb-1">
                Vendor's email address
              </label>
              <input
                id="invite_email"
                type="email"
                required
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="vendor@example.com"
                className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
              {inviteError && <p className="mt-2 text-sm text-red-600">{inviteError}</p>}
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => { setShowInviteModal(false); setInviteEmail(''); setInviteError(null); }}
                  className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 border-2 border-slate-200 rounded-xl hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviteMutation.isPending || !inviteEmail.trim()}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-xl hover:bg-brand-700 disabled:opacity-50"
                >
                  {inviteMutation.isPending ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
